import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trophy,
  Search,
  Users,
  DollarSign,
  Clock,
  Flame,
  ChevronLeft,
  ChevronRight,
  Zap,
  TrendingUp,
  ArrowUpRight,
  Loader2,
  Sparkles,
  Filter,
  SlidersHorizontal,
  Image as ImageIcon,
  Video,
  FileText,
  Mic,
} from "lucide-react";
import { Link, useSearch } from "wouter";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";

// ─── Constants ──────────────────────────────────────────────────

const statusColors: Record<string, string> = {
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  voting: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  completed: "bg-blue-500/20 text-blue-300 border-blue-500/30",
};

const statusLabels: Record<string, string> = {
  active: "Recruiting",
  voting: "Voting",
  completed: "Completed",
};

const gameTypeOptions = [
  { value: "all", label: "All Types" },
  { value: "Creative", label: "Creative" },
  { value: "Prediction", label: "Prediction" },
  { value: "Trivia", label: "Trivia" },
  { value: "Challenge", label: "Challenge" },
  { value: "Tournament", label: "Tournament" },
  { value: "Speed", label: "Speed" },
];

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "popular", label: "Most Popular" },
  { value: "ending_soon", label: "Ending Soon" },
  { value: "prize_pool", label: "Biggest Potential Prize" },
];

const entryFeeOptions = [
  { value: "all", label: "Any ticket price" },
  { value: "free", label: "Free" },
  { value: "under5", label: "Under $5" },
  { value: "5to20", label: "$5 – $20" },
  { value: "over20", label: "$20+" },
];

const submissionIcons: Record<string, any> = {
  image: ImageIcon,
  video: Video,
  text: FileText,
  audio: Mic,
};

// ─── Helpers ────────────────────────────────────────────────────

function formatTimeLeft(endsAt: Date | string | null): string {
  if (!endsAt) return "Open";
  const end = new Date(endsAt);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return "Ended";
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
}

function getEntryFeeRange(filter: string): { min?: number; max?: number } {
  switch (filter) {
    case "free": return { min: 0, max: 0 };
    case "under5": return { min: 1, max: 499 };
    case "5to20": return { min: 500, max: 2000 };
    case "over20": return { min: 2001 };
    default: return {};
  }
}

// ─── Featured Carousel ─────────────────────────────────────────

function FeaturedCarousel() {
  const { data: featured, isLoading } = trpc.discovery.featured.useQuery({ limit: 6 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll, { passive: true });
    return () => el.removeEventListener("scroll", checkScroll);
  }, [checkScroll, featured]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neon" />
      </div>
    );
  }

  if (!featured?.length) return null;

  return (
    <section className="relative">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal to-teal-light flex items-center justify-center">
            <Flame className="w-4 h-4 text-black" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white font-display">Featured Games</h2>
            <p className="text-xs text-slate-400 mt-0.5">Top-rated challenges ready for players</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30"
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 border-white/10 text-slate-400 hover:text-white hover:bg-white/5 disabled:opacity-30"
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {featured.map((game) => (
          <FeaturedCard key={game.id} game={game} />
        ))}
      </div>
    </section>
  );
}

function FeaturedCard({ game }: { game: any }) {
  const SubIcon = submissionIcons[game.submissionType] || ImageIcon;

  return (
    <Link href={`/game/${game.slug}`}>
      <div className="group relative min-w-[320px] max-w-[360px] snap-start cursor-pointer">
        {/* Gradient background card */}
        <div className="relative rounded-xl overflow-hidden border border-white/5 bg-gradient-to-br from-[#0d1520] via-[#111a2e] to-[#0a1628] hover:border-neon/30 transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(0,200,180,0.15)]">
          {/* Top accent bar */}
          <div className="h-1 bg-gradient-to-r from-teal via-teal-light to-gold" />

          <div className="p-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-white truncate group-hover:text-neon transition-colors">
                  {game.title}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <SubIcon className="w-3 h-3" />
                  {game.gameType || "Challenge"}
                  <span className="text-slate-600">·</span>
                  by {game.creatorName}
                </p>
              </div>
              {game.viralScore > 0 && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-neon/10 border border-neon/20 shrink-0">
                  <Zap className="w-3 h-3 text-neon" />
                  <span className="text-xs font-semibold text-neon">{game.viralScore}</span>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="text-xs text-slate-400 line-clamp-2 mb-4 leading-relaxed">
              {game.description || "Join this exciting challenge and compete for prizes!"}
            </p>

            {/* Stats grid */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center p-2 rounded-lg bg-white/[0.03] border border-white/5">
                <DollarSign className="w-3.5 h-3.5 text-gold mx-auto mb-1" />
                <p className="text-sm font-bold text-white font-mono">${game.prizePool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Potential Prize</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-white/[0.03] border border-white/5">
                <Users className="w-3.5 h-3.5 text-neon mx-auto mb-1" />
                <p className="text-sm font-bold text-white font-mono">{game.playerCount}/{game.maxPlayers}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Players</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-white/[0.03] border border-white/5">
                <Clock className="w-3.5 h-3.5 text-amber-400 mx-auto mb-1" />
                <p className="text-sm font-bold text-white">{formatTimeLeft(game.endsAt)}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Time</p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-neon">
                ${((game.entryFee || 0) / 100).toFixed(2)} ticket
              </span>
              <span className="text-xs text-slate-400 group-hover:text-neon transition-colors flex items-center gap-1">
                View Game <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ─── Game Card Grid ─────────────────────────────────────────────

function GameCard({ game }: { game: any }) {
  const SubIcon = submissionIcons[game.submissionType] || ImageIcon;
  const statusColor = statusColors[game.status] || statusColors.active;
  const statusLabel = statusLabels[game.status] || game.status;

  return (
    <Link href={`/game/${game.slug}`}>
      <Card className="group bg-[#111627] border-white/5 hover:border-neon/20 transition-all duration-200 cursor-pointer hover:shadow-[0_0_20px_-5px_rgba(0,200,180,0.1)]">
        <CardContent className="p-0">
          {/* Top accent */}
          <div className="h-0.5 bg-gradient-to-r from-teal/50 to-transparent" />

          <div className="p-4">
            {/* Header row */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-white truncate group-hover:text-neon transition-colors">
                  {game.title}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                  <SubIcon className="w-3 h-3" />
                  {game.gameType || "Challenge"}
                  <span className="text-slate-600">·</span>
                  {game.creatorName}
                </p>
              </div>
              <Badge variant="outline" className={`text-[10px] shrink-0 ${statusColor}`}>
                {statusLabel}
              </Badge>
            </div>

            {/* Description */}
            <p className="text-xs text-slate-400 line-clamp-2 mb-3 leading-relaxed">
              {game.description || "A skill-based challenge with cash prizes."}
            </p>

            {/* Stats row */}
            <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
              <span className="flex items-center gap-1 text-gold font-semibold">
                <DollarSign className="w-3 h-3" />Up to ${game.prizePool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 text-neon" />{game.playerCount}/{game.maxPlayers}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />{formatTimeLeft(game.endsAt)}
              </span>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 border-t border-white/5">
              <span className="text-xs font-mono text-slate-300">
                {game.entryFee === 0 ? "Free" : `$${((game.entryFee || 0) / 100).toFixed(2)} ticket`}
              </span>
              {game.submissionCount > 0 && (
                <span className="text-[10px] text-slate-500">
                  {game.submissionCount} submission{game.submissionCount !== 1 ? "s" : ""}
                </span>
              )}
              <span className="text-xs text-neon opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                Join <ArrowUpRight className="w-3 h-3" />
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Stats Bar ──────────────────────────────────────────────────

function StatsBar() {
  const { data: stats, isLoading } = trpc.discovery.stats.useQuery();

  if (isLoading || !stats) return null;

  const items = [
    { label: "Active Games", value: stats.activeGames, icon: Flame, color: "text-neon" },
    { label: "Total Games", value: stats.totalGames, icon: Trophy, color: "text-gold" },
    { label: "Players", value: stats.totalPlayers, icon: Users, color: "text-neon-light" },
    { label: "Potential Prize Pools", value: `$${stats.totalPrizePool.toLocaleString()}`, icon: DollarSign, color: "text-gold-light" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {items.map((item) => (
        <div
          key={item.label}
          className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5"
        >
          <item.icon className={`w-5 h-5 ${item.color} shrink-0`} />
          <div>
            <p className="text-lg font-bold text-white font-mono leading-none">{item.value}</p>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{item.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────

export default function Games() {
  const { user, isAuthenticated } = useAuth();
  const searchString = useSearch();

  // Seed filters from URL params (e.g. /games?sort=ending_soon&status=active
  // from the "Games for You" cards) so deep links land pre-filtered.
  const urlParams = useMemo(() => new URLSearchParams(searchString), [searchString]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = urlParams.get("status") || "all";
    return ["all", "active", "voting", "completed"].includes(s) ? s : "all";
  });
  const [typeFilter, setTypeFilter] = useState("all");
  const [entryFeeFilter, setEntryFeeFilter] = useState("all");
  const [sortBy, setSortBy] = useState<"newest" | "popular" | "ending_soon" | "prize_pool">(() => {
    const s = urlParams.get("sort");
    return ["newest", "popular", "ending_soon", "prize_pool"].includes(s as any)
      ? (s as any)
      : "newest";
  });
  // Cursor pagination — last seen game id
  const [cursor, setCursor] = useState<number | undefined>(undefined);
  const [accumulatedGames, setAccumulatedGames] = useState<any[]>([]);

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }, []);

  // Build query params
  const feeRange = useMemo(() => getEntryFeeRange(entryFeeFilter), [entryFeeFilter]);
  const queryParams = useMemo(() => ({
    search: debouncedSearch || undefined,
    status: statusFilter as any,
    gameType: typeFilter !== "all" ? typeFilter : undefined,
    entryFeeMin: feeRange.min,
    entryFeeMax: feeRange.max,
    sortBy: sortBy,
    cursor,
    limit: 24,
  }), [debouncedSearch, statusFilter, typeFilter, feeRange, sortBy, cursor]);

  const { data, isLoading, isFetching } = trpc.discovery.list.useQuery(queryParams);

  // Accumulate results for load-more; reset whenever the filter set changes
  useEffect(() => {
    setAccumulatedGames([]);
    setCursor(undefined);
  }, [debouncedSearch, statusFilter, typeFilter, entryFeeFilter, sortBy]);

  useEffect(() => {
    if (!data?.games) return;
    if (cursor === undefined) {
      // Fresh fetch (page 1) — replace
      setAccumulatedGames(data.games);
    } else {
      setAccumulatedGames(prev => {
        const seen = new Set(prev.map(g => g.id));
        return [...prev, ...data.games.filter((g: any) => !seen.has(g.id))];
      });
    }
  }, [data?.games, cursor]);

  const games = data?.nextCursor ? accumulatedGames : (data?.games ?? []);
  const hasActiveFilters = statusFilter !== "all" || typeFilter !== "all" || entryFeeFilter !== "all" || debouncedSearch;

  const clearFilters = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setTypeFilter("all");
    setEntryFeeFilter("all");
    setSortBy("newest");
  };

  const loadMore = () => {
    const lastId = data?.games?.[data.games.length - 1]?.id;
    if (typeof lastId === "number") setCursor(lastId);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-6">
              <Link href="/">
                <span className="text-lg font-bold font-display text-white flex items-center gap-2 cursor-pointer">
                  <Zap className="w-5 h-5 text-neon" />
                  GamePoint
                </span>
              </Link>
              <div className="hidden sm:flex items-center gap-1">
                <Link href="/games">
                  <Button variant="ghost" size="sm" className="text-neon text-xs font-semibold">
                    Discover
                  </Button>
                </Link>
                <Link href="/create">
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs">
                    Create
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Link href="/console">
                  <Button size="sm" variant="outline" className="text-xs border-white/10 text-slate-300 hover:text-white">
                    Console
                  </Button>
                </Link>
              ) : (
                <a href={getLoginUrl()}>
                  <Button size="sm" className="text-xs bg-neon hover:bg-neon/90 text-black font-semibold">
                    Sign In
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-white font-display">
            Discover Games
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Browse skill-based challenges, compete for prizes, and grow your audience
          </p>
        </div>

        {/* Stats */}
        <StatsBar />

        {/* Featured Carousel */}
        <FeaturedCarousel />

        {/* Games for You — Personalized Recommendations */}
        {isAuthenticated && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white font-display flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-400" />
                Games for You
              </h2>
              <span className="text-xs text-slate-500">Based on your activity</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Quick-match cards based on user behavior */}
              <Card className="bg-gradient-to-br from-neon/10 to-transparent border-neon/20 hover:border-neon/40 transition-all cursor-pointer">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-neon" />
                    <span className="text-sm font-bold text-white">Quick Join</span>
                  </div>
                  <p className="text-xs text-slate-400">Games about to start with spots remaining. Jump in before they fill up!</p>
                  <Link href="/games?sort=ending_soon&status=active">
                    <Button size="sm" variant="outline" className="w-full border-neon/30 text-neon hover:bg-neon/10 text-xs mt-1">
                      Find Games Ending Soon
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20 hover:border-amber-500/40 transition-all cursor-pointer">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-bold text-white">Potential Prizes</span>
                  </div>
                  <p className="text-xs text-slate-400">Games with the largest potential prize pools at full capacity.</p>
                  <Link href="/games?sort=prize_pool&status=active">
                    <Button size="sm" variant="outline" className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs mt-1">
                      Browse Potential Prizes
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20 hover:border-purple-500/40 transition-all cursor-pointer">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-bold text-white">Trending Now</span>
                  </div>
                  <p className="text-xs text-slate-400">The most popular games right now. See what everyone is competing in.</p>
                  <Link href="/games?sort=popular&status=active">
                    <Button size="sm" variant="outline" className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10 text-xs mt-1">
                      See What's Hot
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-white/5" />

        {/* Search & Filters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white font-display flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-neon" />
              All Games
            </h2>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-slate-400 hover:text-white"
              >
                Clear filters
              </Button>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search games by name, type, or description..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 bg-[#111627] border-white/10 text-white placeholder:text-slate-500 focus:border-neon/30"
              />
            </div>

            {/* Filter row */}
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-[#111627] border-white/10 text-slate-300 text-xs">
                  <Filter className="w-3 h-3 mr-1.5 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141828] border-white/10">
                  <SelectItem value="all" className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">All Statuses</SelectItem>
                  <SelectItem value="active" className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">Active</SelectItem>
                  <SelectItem value="voting" className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">Voting</SelectItem>
                  <SelectItem value="completed" className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[140px] bg-[#111627] border-white/10 text-slate-300 text-xs">
                  <Trophy className="w-3 h-3 mr-1.5 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141828] border-white/10">
                  {gameTypeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={entryFeeFilter} onValueChange={setEntryFeeFilter}>
                <SelectTrigger className="w-[140px] bg-[#111627] border-white/10 text-slate-300 text-xs">
                  <DollarSign className="w-3 h-3 mr-1.5 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141828] border-white/10">
                  {entryFeeOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={(v) => setSortBy(v as any)}>
                <SelectTrigger className="w-[150px] bg-[#111627] border-white/10 text-slate-300 text-xs">
                  <SlidersHorizontal className="w-3 h-3 mr-1.5 text-slate-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141828] border-white/10">
                  {sortOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-slate-300 focus:text-white focus:bg-white/5 text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Game Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-neon" />
            <p className="text-sm text-slate-400">Loading games...</p>
          </div>
        ) : !data?.games?.length ? (
          <Card className="bg-[#111627] border-white/5">
            <CardContent className="py-16 text-center">
              <Sparkles className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">
                {hasActiveFilters ? "No games match your filters" : "No games available yet"}
              </h3>
              <p className="text-sm text-slate-400 mb-6 max-w-md mx-auto">
                {hasActiveFilters
                  ? "Try adjusting your search or filter criteria to find more games."
                  : "Be the first to create a game and start building your community!"}
              </p>
              {hasActiveFilters ? (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="border-white/10 text-slate-300 hover:text-white"
                >
                  Clear All Filters
                </Button>
              ) : (
                <Link href="/create">
                  <Button className="bg-neon hover:bg-neon/90 text-black font-semibold">
                    Create a Game
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {games.map((game: any) => (
                <GameCard key={game.id} game={game} />
              ))}
            </div>

            {/* Results count + load more */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-slate-500">
                Showing {games.length} game{games.length !== 1 ? "s" : ""}
                {isFetching && <Loader2 className="w-3 h-3 animate-spin inline ml-2" />}
              </p>
              {data?.nextCursor && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-neon/30 text-neon hover:bg-neon/10 gap-1.5"
                  onClick={loadMore}
                  disabled={isFetching}
                >
                  {isFetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Load more
                </Button>
              )}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-slate-500 flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-neon" />
              GamePoint — Skill-based competitions for creators
            </span>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <Link href="/">
                <span className="hover:text-white transition-colors cursor-pointer">Home</span>
              </Link>
              <Link href="/create">
                <span className="hover:text-white transition-colors cursor-pointer">Create Game</span>
              </Link>
              {isAuthenticated && (
                <Link href="/console">
                  <span className="hover:text-white transition-colors cursor-pointer">Console</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
